/**
 * CMS full coverage — the single source of truth for every editable section's
 * built-in (fallback) copy. Every marketing page imports its fallback text FROM
 * HERE (never re-typed inline) and the admin/reseller CMS editors read the same
 * constants for their placeholder text — so a page's default copy and the
 * editor's placeholder can never drift apart.
 *
 * Two shapes, matching the API's value model (see cms.service.ts):
 *   - SINGLE_FALLBACKS[sectionKey]: a flat map of string fields.
 *   - LIST_FALLBACKS[sectionKey]: an array of items (each a flat map of
 *     string / string[] fields) — icons/status/hrefs/step-numbers stay in the
 *     page's own code and are paired with these by array index.
 *
 * A handful of fields are brand/reseller-dependent at runtime (the "Pay your
 * way" pricing assurance, the pricing intro subhead, the two-tone hero
 * headlines on PlatformLanding/Landing) — those stay computed inline on the
 * page exactly as before (so the live marketing copy is unaffected), and this
 * file offers a neutral, non-personalized placeholder string for the editors
 * only (see the `*_PLACEHOLDER` exports at the bottom).
 */

export type CmsListItem = Record<string, string | string[]>;

export const SINGLE_FALLBACKS: Record<string, Record<string, string>> = {
  // ── Company.tsx (itcitysoft.com / itcity.ltd) ──────────────────────────
  'company.hero': {
    eyebrow: 'Software house · Sylhet, Bangladesh',
    headline: 'Local software, built to last.',
    subhead: 'ITCity builds **Possignin** — cloud point of sale with inventory, HRM, CRM, manufacturing, and repair. One calm system, priced in taka, running 100+ shops across Bangladesh.',
  },
  'company.featuresIntro': {
    eyebrow: 'What Possignin does',
    headline: 'One system instead of six.',
    body: 'Most shops juggle a register, a spreadsheet, a notebook, and a chat group. Possignin replaces all of it.',
  },
  'company.productsIntro': {
    eyebrow: 'ITCity products',
    headline: 'Possignin today. More on the way.',
  },
  'company.about': {
    eyebrow: 'About ITCity',
    headline: 'Built in Sylhet, for businesses here.',
    body1: 'ITCity is a team of engineers and designers in Sylhet who believe local businesses deserve software that fits them — priced in taka, fluent in Bangla, and patient enough for a busy counter.',
    body2: 'We started with Possignin because the shops around us were running on notebooks and memory. Today it powers over a hundred businesses, directly and through resellers like Ogrogoti and Nexico. We build slowly and keep what we ship excellent.',
  },
  // Registered details for the payment gateway's merchant-site requirement. Editable by
  // an admin via Marketing content → Company → Registered company details.
  'company.details': {
    legalName: 'ITCity',
    registration: 'Trade License No. 1230038850, issued by Sylhet City Corporation',
    address: 'Sylhet, Bangladesh',
    phone: '+880 1754-800757',
    email: 'support@itcity.ltd',
    hours: 'Saturday–Thursday, 10:00–19:00 (Asia/Dhaka)',
  },
  'company.footerTagline': { tagline: 'Software house · Sylhet, Bangladesh' },
  'company.footerHeadings': { product: 'Product', company: 'Company', legal: 'Legal' },
  'company.footerMeta': {
    copyright: '© 2026 ITCity. All rights reserved.',
    paymentsLabel: 'We accept',
    statusLabel: 'All systems operational',
  },
  'company.plansPreview': { headline: 'Pricing that scales with your shop.' },

  // ── PlatformLanding.tsx (possignin.com) ────────────────────────────────
  'product.hero': {
    // headline here is a PLACEHOLDER hint only (the editor never pre-fills a
    // SINGLE field's real value) — leaving the field blank keeps the page's
    // own two-tone <h1> JSX ("The POS that runs your whole business.").
    headline: 'The POS that runs your whole business.',
    subhead: 'Sales, inventory, HRM, CRM, manufacturing, and repair — one calm system, priced in taka. Built in Bangladesh, for the way Bangladesh does business.',
  },
  'product.announcement': { text: 'Customer ledgers & cash collection', href: '/blog' },
  'product.featuresIntro': {
    eyebrow: 'Everything in one place',
    headline: 'One system instead of six.',
    body: 'Most shops juggle a register, a spreadsheet, a notebook, and a chat group. Possignin replaces all of it with software that actually fits how you work.',
  },
  'product.builtForBdIntro': {
    eyebrow: 'Built for Bangladesh',
    headline: 'Not a Western tool, translated.',
    body: 'Possignin was built in Sylhet for shops here — the currency, the languages, the payment habits, and the support are all local.',
  },
  'product.howItWorksIntro': { eyebrow: 'How it works', headline: 'Live by this afternoon.' },
  'product.resellerBand': {
    eyebrow: 'Partner program',
    headline: 'Sell Possignin under your own brand.',
    body: 'Become a reseller and earn commission on every business you bring in. Your domain, your branding, your pricing — our infrastructure. Ogrogoti and Nexico already do.',
  },
  'product.testimonial': {
    eyebrow: 'Why shops switch',
    quote: 'Most shops come to Possignin from a notebook and a spreadsheet. They stay because the numbers finally live in one place — and the evening close takes minutes, not hours.',
    attribution: '106 businesses across Bangladesh run on Possignin — directly and through resellers.',
  },
  'product.faqIntro': { heading: 'Questions, answered.' },
  'product.finalCta': {
    headline: 'Run your shop the calm way.',
    body: 'Free for 7 days. Setup takes five minutes. No card needed.',
  },
  'product.plansPreview': { headline: 'Pricing that scales with your shop.' },

  // ── Pricing.tsx ─────────────────────────────────────────────────────────
  'pricing.intro': {
    headline: 'Simple pricing, in taka.',
    // Placeholder hint only — the live page computes its own brand-aware
    // subhead inline (platform vs. reseller copy); this is the platform variant.
    subhead: 'Start free for 7 days, no card. Pick a plan when you’re ready. Pay online with SSLCommerz or cash to our Sylhet office.',
  },
  'pricing.contactLine': { text: 'Need more locations or users?' },

  // ── Features.tsx ────────────────────────────────────────────────────────
  'features.intro': {
    eyebrow: 'Features',
    headline: 'Everything a growing business runs on.',
    subhead: 'Possignin is six tools in one — so your counter, your stockroom, your staff, and your accounts all speak the same language.',
  },
  'features.dataBanner': { text: 'Your data stays in **your own database** — exportable any time, never sold.' },

  // ── About.tsx ───────────────────────────────────────────────────────────
  'about.intro': {
    eyebrow: 'About',
    headline: 'Software for the shops we grew up around.',
    body1: 'Possignin is built by **ITCity**, a software team in Sylhet, Bangladesh. We started Possignin because the shops around us — bakeries, pharmacies, electronics stores, repair workshops — were running on notebooks, calculators, and memory.',
    body2: 'The tools that existed were either too expensive, too complicated, or built for somewhere else. So we made one that fits here: priced in taka, fluent in Bangla, and patient enough for a busy counter.',
    body3: 'Today Possignin runs over a hundred businesses — directly and through resellers who sell it under their own brand — and it grows the way our customers tell us to.',
  },
  'about.cta': { locationTag: 'Sylhet, Bangladesh', headline: 'Want to build your business on Possignin?' },

  // ── Blog.tsx ────────────────────────────────────────────────────────────
  'blog.intro': {
    headline: 'Newsroom',
    subhead: 'Announcements, product notes, and practical writing on running a better shop in Bangladesh.',
  },
  'blog.notFound': { text: 'That story doesn’t exist.' },

  // ── Landing.tsx (reseller home) — all reseller-editable ────────────────
  'landing.hero': {
    eyebrow: 'Built for Bangladesh',
    // Placeholder hint only — leaving the field blank keeps the page's own
    // "Run your shop with {your name} — built for Bangladesh." styled <h1>.
    headline: 'Run your shop with your business — built for Bangladesh.',
    subhead: 'Cloud POS with HRM, CRM, manufacturing, repair, and WooCommerce sync. Pay in BDT, get cash or SSLCommerz. Built for shops, restaurants, pharmacies, and workshops.',
  },
  'landing.cta': { text: 'Start free trial' },
  'landing.featuresIntro': { headline: 'Everything your shop needs.' },
  'landing.howItWorksIntro': { eyebrow: 'Get started', headline: 'Selling in three steps.' },
  'landing.plansPreview': { headline: 'Simple plans in taka.' },
  'landing.faqIntro': { heading: 'Questions, answered.' },
  'landing.ctaStrip': { headline: 'Ready to get started?', body: 'Free for 7 days, no card needed. Setup takes 5 minutes.' },
  'landing.footerTagline': { tagline: 'Cloud POS & business software. Priced in taka, fluent in Bangla.' },
  'landing.footerHeadings': { product: 'Product', legal: 'Legal' },
  // `copyright` here is the EDITOR's placeholder only. The live reseller footer
  // computes `© <year> <brand name>` from the resolved brand when the field is
  // blank — a hardcoded "ITCity" default would render on every white-label
  // domain that hasn't set its own (the leak RESELLER_LIST_OVERRIDES exists to
  // prevent). Never put a platform brand name in this value.
  'landing.footerMeta': {
    copyright: '© 2026 Your brand. All rights reserved.',
    statusLabel: 'All systems operational',
  },

  // ── Contact.tsx (public contact page — brand-aware) ─────────────────────
  // address/hours/phone default EMPTY: the page hides an unset row rather than
  // inventing facts. Platform hosts inject the real ITCity details in the
  // component (same brand-computed pattern as the pricing subhead); a reseller
  // fills their own via the CMS.
  'contact.info': {
    headline: 'Talk to us.',
    subhead: 'Questions about plans, setup, or moving your shop over — a human replies within one business day.',
    phone: '',
    address: '',
    hours: '',
  },

  // ── MarketingFooter.tsx — platform footer variant (admin-only) ─────────
  'shared.footerTagline': {
    tagline: 'Cloud POS & business software, built in Sylhet, Bangladesh — by ITCity. Priced in taka, fluent in Bangla.',
  },
  'shared.footerHeadings': { product: 'Product', company: 'Company', legal: 'Legal' },
  'shared.footerMeta': {
    copyright: '© 2026 ITCity. All rights reserved.',
    paymentsLabel: 'We accept',
    statusLabel: 'All systems operational',
  },

  // ---- Legal page headers (title / last-updated / intro) --------------------
  'legal.termsMeta': { title: 'Terms of Service', updated: '14 June 2026', intro: 'The agreement for using Possignin — written to be read, not just signed.' },
  'legal.privacyMeta': { title: 'Privacy Policy', updated: '14 June 2026', intro: 'Your business data is yours. This policy explains, in plain terms, what Possignin collects, how we use it, and the control you keep.' },
  'legal.refundMeta': { title: 'Refund Policy', updated: '26 July 2026', intro: 'Fair and clear: a free trial first, refunds when we get billing wrong, approved refunds completed within 7–10 working days, and no surprises.' },
  'legal.cookiesMeta': { title: 'Cookie Policy', updated: '14 June 2026', intro: 'We use as few cookies as possible — to keep you signed in, remember your preferences, and count visits.' },
};

export const LIST_FALLBACKS: Record<string, CmsListItem[]> = {
  // ── Company.tsx ─────────────────────────────────────────────────────────
  'company.heroBadges': [{ text: '7-day free trial' }, { text: 'No card required' }, { text: 'Setup in 5 minutes' }, { text: 'Bangla + English' }],
  'company.features': [
    { title: 'Sales & inventory', body: 'Fast sell screen, FIFO and lot tracking, barcodes, and low-stock alerts across locations.' },
    { title: 'BDT-native billing', body: 'SSLCommerz built in, cash-to-office in Sylhet, auto invoices and receipts — all in taka.' },
    { title: 'HRM & payroll', body: 'Staff, shifts, attendance, leave, and payroll — the people side of your shop, handled.' },
    { title: 'CRM & contacts', body: 'Customers, suppliers, dues, and follow-ups. Know who owes what at a glance.' },
    { title: 'Manufacturing', body: 'Recipes, ingredients, and production runs for bakeries, kitchens, and workshops.' },
    { title: 'Repair & service', body: 'Job sheets, device history, and service status for repair shops and workshops.' },
    { title: 'Reports that read', body: 'Daily sales, P&L, stock, and tax — plain reports you can act on. Export any time.' },
    { title: 'Runs on a phone', body: 'Android, iPhone, or any browser. Receipt printers and barcode scanners supported.' },
    { title: 'Calm & secure', body: '2FA, audit logs, role-based access. Your data lives in your own database — never sold.' },
  ],
  'company.products': [
    { name: 'Possignin', tag: 'Cloud POS', body: 'Point of sale and full business management. Our flagship — running 100+ shops today.' },
    { name: 'ITCity School', tag: 'School management', body: 'Admissions, attendance, fees, and results — the same calm software, for education.' },
    { name: 'ITCity CRM', tag: 'Sales & relationships', body: 'Leads, pipelines, and follow-ups for service businesses built on relationships.' },
  ],

  // Placeholder leadership entries for the payment gateway's merchant-site requirement.
  // Clearly marked as placeholders so nobody mistakes them for real named officers —
  // an admin replaces them via Marketing content → Company → Management / leadership.
  'company.management': [
    { name: 'To be updated', role: 'Managing Director', bio: 'Add the name and a short bio for the company’s managing director.' },
    { name: 'To be updated', role: 'Head of Engineering', bio: 'Add the name and a short bio for the engineering lead.' },
  ],

  // ── PlatformLanding.tsx ─────────────────────────────────────────────────
  'product.heroBadges': [{ text: '7-day free trial' }, { text: 'No card required' }, { text: 'Setup in 5 minutes' }, { text: 'Bangla + English' }],
  'product.trustStats': [
    { value: '106', label: 'businesses running' },
    { value: '4.9 GiB', label: 'live transaction data' },
    { value: '৳', label: 'native BDT billing' },
    { value: '24/7', label: 'support from Sylhet' },
  ],
  'product.features': [
    { title: 'Sales & inventory', body: 'Ring up sales fast, track every item. FIFO, lot tracking, barcodes, and stock alerts out of the box.' },
    { title: 'BDT-native billing', body: 'SSLCommerz built in, cash-to-office in Sylhet, auto invoices and receipts — all in taka.' },
    { title: 'HRM & payroll', body: 'Staff, shifts, attendance, leave, and payroll — the people side of your shop, handled.' },
    { title: 'CRM & contacts', body: 'Customers, suppliers, dues, and follow-ups. Know who owes what at a glance.' },
    { title: 'Manufacturing', body: 'Recipes, ingredients, and production runs for bakeries, kitchens, and workshops.' },
    { title: 'Repair & service', body: 'Job sheets, device tracking, and service status for repair shops and workshops.' },
    { title: 'Reports that read', body: 'Daily sales, P&L, stock, and tax — plain reports you can act on. Export any time.' },
    { title: 'Runs on a phone', body: 'Android, iPhone, or any browser. Receipt printers and barcode scanners supported.' },
    { title: 'Calm & secure', body: '2FA, audit logs, role-based access. Your data lives in your own database — never sold.' },
  ],
  'product.builtForBd': [
    { title: 'Taka, not dollars', body: 'Every price, invoice, and report is in BDT. SSLCommerz for online; cash collection to our Sylhet office for the rest.' },
    { title: 'Bangla + English', body: 'Switch language per user. Receipts, reports, and the POS follow — no half-translated screens.' },
    { title: 'Made for local rules', body: 'VAT-ready invoicing and the receipt formats your customers expect. No Western-only assumptions.' },
  ],
  'product.howItWorks': [
    { title: 'Sign up free', body: 'Create your business in minutes — no card, no sales call. Pick a plan when the trial ends.' },
    { title: 'Set up your shop', body: 'Add products, staff, and locations. Import a CSV or start fresh. We help if you get stuck.' },
    { title: 'Start selling', body: 'Ring up sales on any device. Watch the reports fill in. Pay in taka, online or cash.' },
  ],
  'product.faq': [
    { q: 'Do I need a card to start?', a: 'No. The 7-day trial needs no card. You pick a plan and pay in taka only when the trial ends.' },
    { q: 'Can I use it in Bangla?', a: 'Yes. Each user can switch between Bangla and English; receipts and reports follow the choice.' },
    { q: 'How do I pay?', a: 'SSLCommerz for online payments, or cash to our Sylhet office. Your subscription stays active either way.' },
    { q: 'Where does my data live?', a: 'In your own database. We never sell your data, and you can export it any time.' },
    { q: 'What if I sell software too?', a: 'Apply to our reseller program and sell Possignin under your own brand and pricing.' },
  ],

  // ── Pricing.tsx ─────────────────────────────────────────────────────────
  'pricing.assurances': [
    { title: '7-day free trial', body: 'No card needed to start. Add a payment method any time during the trial.' },
    { title: 'Switch any time', body: 'Upgrade or downgrade whenever you like — we prorate the difference automatically.' },
    {
      title: 'Pay your way',
      // Placeholder hint only (platform variant) — the live page computes its
      // own brand-aware body inline (reseller variant mentions the reseller by name).
      body: 'SSLCommerz for online, or cash to our Sylhet office. Resold by a partner? Pay them in cash.',
    },
  ],

  // ── Features.tsx ────────────────────────────────────────────────────────
  'features.groups': [
    {
      title: 'Point of sale',
      items: [
        'Fast sell screen for counter, table, or field',
        'Barcode scanning and label printing',
        'Receipt printers, cash drawers, and weighing scales',
        'Discounts, returns, holds, and split payments',
      ],
    },
    {
      title: 'Inventory',
      items: [
        'Real-time stock across multiple locations',
        'FIFO cost and lot / batch tracking',
        'Purchase orders and supplier management',
        'Low-stock alerts and stock-take counts',
      ],
    },
    {
      title: 'People & customers',
      items: [
        'Staff, roles, shifts, attendance, and leave',
        'Payroll with allowances and deductions',
        'Customer and supplier ledgers with dues',
        'CRM follow-ups, leads, and proposals',
      ],
    },
    {
      title: 'Money',
      items: [
        'BDT-native invoicing and receipts',
        'SSLCommerz online payments + cash collection',
        'Expenses, accounts, and cash registers',
        'VAT-ready tax handling',
      ],
    },
    {
      title: 'Make & repair',
      items: [
        'Manufacturing recipes and production runs',
        'Ingredient and raw-material tracking',
        'Repair job sheets and device history',
        'Service status from intake to delivery',
      ],
    },
    {
      title: 'Insight & control',
      items: [
        'Daily sales, profit & loss, and stock reports',
        'Audit log of who did what, and when',
        'Two-factor authentication and device sessions',
        'WooCommerce sync for your online store',
      ],
    },
  ],

  // ── About.tsx ───────────────────────────────────────────────────────────
  'about.values': [
    { title: 'Local first', body: 'We build for Bangladeshi shops — the currency, the languages, the way trade actually happens here.' },
    { title: 'Calm software', body: 'Restraint over clutter. Software that helps you close up faster, not one more thing to manage.' },
    { title: 'Real support', body: 'A team you can call, in Sylhet, who understands your business — not a ticket queue abroad.' },
  ],

  // ── Blog.tsx — slug drives routing; changing a slug breaks existing links.
  'blog.posts': [
    {
      slug: 'announcing-per-business-databases',
      title: 'Announcing per-business dedicated databases',
      excerpt: 'Every new business can now run on its own isolated database — faster, safer, and ready for scale.',
      date: '14 June 2026',
      category: 'Announcements',
      readMins: '3',
      body: [
        'Until now, businesses on Possignin shared one large database, kept apart by strict isolation. It worked — but as shops grew, we wanted more headroom and stronger guarantees.',
        'Starting today, every new business can be provisioned its own dedicated database. Your data sits alone, backups are per-business, and a busy neighbour can never slow you down. If you prefer, you can even bring your own database server.',
        'Existing businesses keep working exactly as before, and can move to a dedicated database whenever they’re ready. Nothing changes unless you want it to.',
      ],
    },
    {
      slug: 'stop-running-your-shop-on-a-notebook',
      title: 'Stop running your shop on a notebook',
      excerpt: 'The hidden cost of paper ledgers — and the five numbers you should see every evening.',
      date: '12 June 2026',
      category: 'Playbook',
      readMins: '4',
      body: [
        'A notebook never tells you that your best-selling item is about to run out, or that one customer’s dues have quietly grown for three months. It just records — it never warns.',
        'When you move to a POS, the first thing you get back is the evening close. Instead of adding up slips by hand, you see today’s sales, your cash in drawer, what sold, and what’s low — in one screen, before you lock up.',
        'The five numbers worth watching every day: total sales, cash collected, top item, low-stock count, and outstanding dues. Possignin puts all five on your dashboard, so the close takes a minute, not an hour.',
      ],
    },
    {
      slug: 'taka-first-billing',
      title: 'Why billing should be taka-first, not dollar-translated',
      excerpt: 'Rounding, SSLCommerz, and cash-to-office — the small things that make local billing actually work.',
      date: '5 June 2026',
      category: 'Product',
      readMins: '3',
      body: [
        'Most POS software was built for dollars and bolted on other currencies later. You feel it in the rounding, the receipt formats, and the payment options that don’t include the ones your customers actually use.',
        'Possignin is taka-first. Prices, invoices, and reports are in BDT from the ground up. Online payments go through SSLCommerz; for everything else, you can pay your subscription as cash to our Sylhet office.',
        'It sounds small. But when the numbers match what’s in the drawer to the poisha, you stop second-guessing the software — and start trusting it.',
      ],
    },
    {
      slug: 'one-system-instead-of-six',
      title: 'One system instead of six',
      excerpt: 'Your register, spreadsheet, payroll book, and chat group are four versions of the truth. Here’s how to get to one.',
      date: '28 May 2026',
      category: 'Playbook',
      readMins: '5',
      body: [
        'Every growing shop ends up with the same mess: a register for sales, a spreadsheet for stock, a notebook for staff, and a chat group for everything else. Each holds a slightly different version of the truth.',
        'The fix isn’t more discipline — it’s fewer places. When sales, inventory, staff, and customers live in one system, a sale updates stock, stock triggers a reorder, and the day’s total is just there.',
        'You don’t have to switch everything at once. Start with sales and inventory, get comfortable, then add HRM, CRM, or manufacturing when you’re ready. Possignin grows in the order that suits you.',
      ],
    },
    {
      slug: 'bangla-first-receipts',
      title: 'Bangla-first receipts, end to end',
      excerpt: 'Switch language per user and watch receipts, reports, and the POS follow — no half-translated screens.',
      date: '18 May 2026',
      category: 'Product',
      readMins: '2',
      body: [
        'Language shouldn’t be an afterthought. In Possignin, each user picks Bangla or English, and the choice carries everywhere — the sell screen, the reports, and the printed receipt your customer takes home.',
        'We use a proper Bangla webfont so receipts print cleanly on thermal printers, and numbers stay readable. No tofu boxes, no broken characters.',
      ],
    },
    {
      slug: 'reseller-program',
      title: 'Sell Possignin under your own brand',
      excerpt: 'How the reseller program works — your domain, your pricing, our infrastructure.',
      date: '12 May 2026',
      category: 'Partners',
      readMins: '3',
      body: [
        'If you already sell software or services to local businesses, the reseller program lets you offer a full POS under your own brand — without building one.',
        'You get a branded subdomain (or your own domain), your own packages and pricing above a fair floor, and a panel to manage your customers. We handle the platform, the payments, and the uptime.',
        'You earn commission on every business you bring in, paid on a regular cycle. Apply through “Become a reseller” and we usually review within 1–2 business days.',
      ],
    },
  ],

  // ── Landing.tsx (reseller home) ─────────────────────────────────────────
  // NOTE white-label fix: the ITCity-specific "Cash to office in Sylhet" facts
  // are replaced with neutral copy — see docs/superpowers/sdd/cms-coverage-report.md.
  'landing.heroBadges': [{ text: '7-day trial' }, { text: 'Pay online or in cash' }, { text: 'Setup in 5 min' }, { text: 'Bangla + English UI' }],
  // The COMPLETE product capability list (one card per real POS area/module) —
  // icons pair by position in Landing.tsx's FEATURE_ICONS.
  'landing.features': [
    { title: 'Sales & POS terminal', body: 'Ring up sales on any device — barcode scanning, receipts, returns and exchanges, cash, card, and mobile payments.' },
    { title: 'Inventory & stock', body: 'Real-time stock by location with FIFO lot tracking, low-stock alerts, and full stock-count (stock taking) workflows.' },
    { title: 'Purchases & suppliers', body: 'Purchase orders, goods receiving, supplier ledgers and dues — your buying side in one place.' },
    { title: 'Customers & CRM', body: 'Customer profiles, dues and credit limits, follow-ups and leads — keep regulars coming back.' },
    { title: 'Expenses & accounts', body: 'Track expenses, cash registers, and account books alongside sales, so the day balances.' },
    { title: 'Reports that read', body: 'Daily sales, profit & loss, stock, tax, and staff reports. Export to CSV any time.' },
    { title: 'Multi-location', body: 'Branches, warehouses, and counters under one account — stock and reports per location or combined.' },
    { title: 'Staff, roles & audit', body: 'Role-based permissions per staff member, per-cashier sales, and a full audit trail.' },
    { title: 'Repair & service jobs', body: 'Job tickets with status tracking, technician assignment, and service invoicing for workshops.' },
    { title: 'Manufacturing', body: 'Recipes and bills of materials, production runs, and ingredient costing on manufacturing plans.' },
    { title: 'Returns & warranty (RMA)', body: 'Customer and supplier return workflows with warranty tracking, exchanges, and refunds.' },
    { title: 'E-commerce sync', body: 'Connect your online store — products, stock, and orders stay in sync with the shop floor.' },
  ],
  'landing.trustStats': [
    { value: '7 days', label: 'Free trial, no card' },
    { value: '5 min', label: 'Typical setup time' },
    { value: '৳ BDT', label: 'Local pricing & payments' },
    { value: 'বাংলা', label: 'Bangla + English UI' },
  ],
  'landing.howItWorks': [
    { title: 'Start your free trial', body: 'Sign up in minutes — no card needed. Your shop gets its own secure POS.' },
    { title: 'Set up your shop', body: 'Add products, locations, and staff. Import your existing product list to get going fast.' },
    { title: 'Sell everywhere', body: 'Take cash or card on any device, and watch stock and reports update live.' },
  ],
  'landing.faq': [
    { q: 'How long is the free trial?', a: 'Seven days with the full product — no card required. Pick a plan whenever you’re ready.' },
    { q: 'How do I pay?', a: 'Pay online with SSLCommerz (cards, mobile banking, internet banking) — or pay us directly in cash.' },
    { q: 'Can I use it on my phone?', a: 'Yes — the POS runs in any browser on Android, iPhone, or PC. Receipt printers and barcode scanners are supported.' },
    { q: 'Is my data safe?', a: 'Your business runs with isolated data, role-based access, and a full audit log. Your data is yours — we never sell it.' },
    { q: 'Can I change plans later?', a: 'Any time. Upgrades take effect immediately; downgrades apply at your next renewal.' },
    { q: 'Do you support Bangla?', a: 'Fully — the app works in Bangla and English, and each user picks their own language.' },
  ],

  // ---- Legal pages (terms / privacy / refund / cookies) --------------------
  // Moved out of the page files so the admin CMS editor can SHOW and edit the real
  // default copy instead of an empty box. Each item is one titled section.
  'legal.terms': [
    {
      heading: 'Agreement',
      body: [
        'These terms are between you and ITCity (Sylhet, Bangladesh), the operator of Possignin. By creating an account or using the service, you agree to them.',
        'If you use Possignin on behalf of a business, you confirm you are authorised to accept these terms for that business.',
      ],
    },
    {
      heading: 'The service',
      body: [
        'Possignin is a cloud point-of-sale and business management service: sales, inventory, HRM, CRM, manufacturing, repair, and reporting, available on the web and supported devices.',
        'We work to keep the service available and improving, but we provide it on an "as available" basis and may add, change, or remove features.',
      ],
    },
    {
      heading: 'Your account',
      body: [
        'Keep your login details secure; you are responsible for activity under your account. Tell us promptly if you suspect unauthorised access.',
        'You must give accurate information and keep it up to date. One person is responsible for billing on each business account.',
      ],
    },
    {
      heading: 'Acceptable use',
      body: [
        'Do not use Possignin to break the law, infringe others’ rights, send spam, or attempt to disrupt or gain unauthorised access to the service.',
        'You are responsible for the lawfulness of the data you enter, including your customers’ information.',
      ],
    },
    {
      heading: 'Subscriptions & billing',
      body: [
        'Paid plans are billed in Bangladeshi Taka (BDT). You can pay online via SSLCommerz or by cash to our Sylhet office.',
        'New businesses get a 7-day free trial. After the trial, a plan must be active to keep using the service.',
        'If a payment is missed, we will remind you. After the grace period, the business may be suspended until payment is made; your data is retained during suspension.',
      ],
    },
    {
      heading: 'Your data',
      body: [
        'You own the business data you put into Possignin. We process it to provide the service, as described in our Privacy Policy.',
        'You can export your data at any time. On account closure, we delete it within 30 days unless we must keep records by law.',
      ],
    },
    {
      heading: 'Intellectual property',
      body: [
        'Possignin, its software, and its branding belong to ITCity. These terms do not transfer any of our intellectual property to you.',
        'Resellers may use the Possignin platform under a separate reseller agreement, with their own branding as permitted there.',
      ],
    },
    {
      heading: 'Liability & law',
      body: [
        'To the extent allowed by law, our total liability for any claim is limited to the fees you paid in the three months before the claim.',
        'These terms are governed by the laws of Bangladesh, and disputes are subject to the courts of Sylhet.',
      ],
    },
    {
      heading: 'Changes',
      body: [
        'We may update these terms; the new version will be posted here with an updated date, and material changes will be notified in the app. Continuing to use Possignin means you accept the change.',
      ],
    },
  ],
  'legal.privacy': [
    {
      heading: 'Who we are',
      body: [
        'Possignin is a cloud point-of-sale and business management service operated by ITCity, based in Sylhet, Bangladesh. This policy explains what we collect, why, and the choices you have.',
        'It applies to the Possignin SaaS app and the Possignin POS, whether you use Possignin directly or through one of our resellers.',
      ],
    },
    {
      heading: 'What we collect',
      body: [
        'Account data: your name, email or username, phone number, and password (stored only as a hash — we never store your plain password).',
        'Business data you enter: products, customers, suppliers, staff, sales, invoices, and reports for your business.',
        'Payment data: when you pay a subscription, our gateway SSLCommerz processes the card or mobile-wallet details. We receive only the result (paid / failed) and a reference — never your full card number.',
        'Technical data: IP address, device and browser type, and basic usage logs, used to keep the service secure and working.',
      ],
    },
    {
      heading: 'How we use it',
      body: [
        'To run the service: process your sales, keep your subscription active, and show your reports.',
        'To support you: answer questions and fix problems you report.',
        'To keep things safe: detect fraud, abuse, and security issues, and meet our legal obligations.',
        'We do not use your business data to train advertising models, and we never sell it.',
      ],
    },
    {
      heading: 'Where your data lives',
      body: [
        'Your operational data is stored in a database for your business, hosted on our infrastructure in Bangladesh. New businesses can be given a dedicated database; some are hosted on your own server if you choose the self-hosted option.',
        'We keep nightly backups and use encryption in transit (HTTPS) for everything between your browser and our servers.',
      ],
    },
    {
      heading: 'Sharing',
      body: [
        'We share data only with: SSLCommerz (to take payments), and a reseller — if you signed up under one — limited to what they need to manage your account.',
        'We may disclose data if required by Bangladeshi law or a valid legal request. We will tell you when we are allowed to.',
      ],
    },
    {
      heading: 'Your choices',
      body: [
        'You can access and export your business data at any time from inside the app.',
        'You can ask us to delete your account and data; we will do so within 30 days, except where we must keep records for legal or accounting reasons.',
        'You can turn off non-essential cookies — see our Cookie Policy.',
      ],
    },
    {
      heading: 'Changes & contact',
      body: [
        'We may update this policy; we will post the new version here and update the date above. Material changes will be notified in the app.',
        'For any privacy question, email support@itcitysoft.com.',
      ],
    },
  ],
  'legal.refund': [
    {
      heading: 'Try before you pay',
      body: [
        'Every new business starts with a 7-day free trial. No card is needed and nothing is charged during the trial, so you can decide before any money changes hands.',
      ],
    },
    {
      heading: 'Monthly and yearly plans',
      body: [
        'Subscriptions are paid in advance for the period you choose (monthly or yearly), in Bangladeshi Taka.',
        'If you cancel, your plan stays active until the end of the period you already paid for. We do not auto-charge a cancelled plan for the next period.',
      ],
    },
    {
      heading: 'When we give refunds',
      body: [
        'If you were charged in error — for example, billed twice for the same period — we refund the incorrect charge in full.',
        'If the service was unavailable for an extended, verified outage caused by us, we credit or refund a fair share of the affected period.',
        'Within 7 days of your first paid charge, if Possignin is not right for you, contact us and we will refund that first payment.',
      ],
    },
    {
      heading: 'When we usually don’t',
      body: [
        'We generally do not refund a period that has already been used, or a yearly plan part-way through, beyond the cases above.',
        'Add-ons and one-time purchases (such as SMS packs) are non-refundable once delivered.',
      ],
    },
    {
      heading: 'How long a refund takes',
      body: [
        'Once your refund or return claim is approved, we complete it within 7–10 working days.',
        'We acknowledge every claim within 2 working days and tell you at that point whether it is approved, so you are never left waiting without an answer.',
        'The 7–10 working days is our side of the process. For card and mobile-wallet payments your bank or wallet provider may take a few additional days to post the money to your account, which is outside our control.',
      ],
    },
    {
      heading: 'How refunds are made',
      body: [
        'Online payments made through SSLCommerz are refunded to the original card or wallet they were paid from.',
        'Cash payments are refunded by bank transfer or cash at our Sylhet office.',
      ],
    },
    {
      heading: 'How to request',
      body: [
        'Email support@itcitysoft.com from your account email with your business name and the charge in question. We respond within 2 working days, and complete an approved refund within 7–10 working days.',
      ],
    },
  ],
  'legal.cookies': [
    {
      heading: 'What cookies are',
      body: [
        'Cookies are small files a website stores in your browser. We use a small number of them, and only for the reasons below.',
      ],
    },
    {
      heading: 'Essential cookies',
      body: [
        'These keep you signed in and the app working. We set a secure, httpOnly sign-in cookie so you stay logged in across page reloads — it can’t be read by JavaScript, which protects your session.',
        'Essential cookies can’t be turned off, because the service won’t work without them.',
      ],
    },
    {
      heading: 'Preference cookies',
      body: [
        'These remember choices like light or dark mode and your language (Bangla or English), so the app looks the way you left it.',
      ],
    },
    {
      heading: 'Analytics',
      body: [
        'We use privacy-respecting analytics to count visits and understand which features are used, so we can improve Possignin. These do not identify you personally and are not used for advertising.',
        'We do not run third-party advertising cookies or sell data to ad networks.',
      ],
    },
    {
      heading: 'Your control',
      body: [
        'You can clear or block cookies in your browser settings. Blocking essential cookies will sign you out and stop the app from working; blocking analytics or preference cookies is fine and won’t break anything.',
      ],
    },
    {
      heading: 'Contact',
      body: [
        'Questions about cookies? Email support@itcitysoft.com.',
      ],
    },
  ],
};

/**
 * Reseller-editor overrides — white-label safety valve for LIST sections only.
 *
 * A LIST section with no saved row pre-fills the editor with its REAL fallback
 * items (editable starting content — there's no sane "blank textarea" analogue
 * for a repeatable card list), so an admin/reseller who hits Save without
 * changing anything persists that text verbatim. `pricing.assurances` is
 * `resellerEditable`, but its platform fallback ("...cash to our Sylhet
 * office...") is an ITCity-specific fact — exactly the leak the Landing.tsx
 * neutral-copy fix addresses. `landing.heroBadges` / `landing.features` don't
 * need an entry here because their shared fallback text is already neutral at
 * the source. The platform admin editor and the live platform Pricing page
 * both keep using `LIST_FALLBACKS` unchanged (correct for possignin.com); only
 * the reseller Content editor's pre-fill draws from here when present.
 */
export const RESELLER_LIST_OVERRIDES: Record<string, CmsListItem[]> = {
  'pricing.assurances': [
    { title: '7-day free trial', body: 'No card needed to start. Add a payment method any time during the trial.' },
    { title: 'Switch any time', body: 'Upgrade or downgrade whenever you like — we prorate the difference automatically.' },
    { title: 'Pay your way', body: 'SSLCommerz for online, or pay us directly in cash.' },
  ],
};
